// handles form state, validates data, submits + onSubmit
import React, { useEffect, useState } from 'react';
import logger from '@gral/log';
import zod from 'zod';
import { useSetState } from 'react-use';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import { Button, Grid, onChangeObject, TNormalizer, TTextType, Typography } from '@ts/storybook';
import { GridContainerFullHeight } from '@ts/storybook';
import { CEntity, IEntity } from '@ts/ui-models';
import { useAppDispatch } from '@redux/store';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import { decommissionData } from '@redux/slices/functionals/request/requestActions';
import { get, set } from 'lodash';
import { useRouterContext } from '@utils/context/RouterContext';
import AuditDataEntity from '@components/FormContainer/AuditDataEntity';

interface FormContainerProps {
  form: any; //Component to be rendered
  zodValidationSchema: zod.ZodSchema; //ZOD validation
  validationSchema: any; //YUP validation
  initialValues?: CEntity<IEntity> | any;
  essentialValues?: Set<string>; //keys that are required
  normalize?: TNormalizer<any>;
  onCancel?: () => void;
  onSubmit: (v: any) => void;
  isSubmiting?: boolean;
  disabledNotifyChanges?: boolean; //To hide warning and audit
  renderActionButtons?: () => React.ReactNode;
  submitButtonCaption?: string;
  cancelButtonCaption?: string;
  methodCRUD?: 'put' | 'patch';
}

const log = logger('FormContainer');
const FormContainer = (props: FormContainerProps) => {
  log.info('props', props);
  const dispatch = useAppDispatch();
  const { mode } = useRouterContext();

  const {
    disabledNotifyChanges = true,
    renderActionButtons = () => null,
    submitButtonCaption = 'Save',
    cancelButtonCaption = 'Cancel',
    methodCRUD = 'patch',
    normalize,
  } = props;
  const [state, setState] = useSetState<{ requiredFields: string[]; isReadyToSubmit: boolean }>({
    isReadyToSubmit: true,
    requiredFields: [],
  });
  const [values, setValues] = useState(props.initialValues);
  const [keyValuesUpdated, setKeyValuesUpdated] = useState(new Set<string>());

  const { form: ChildForm } = props;
  const { requiredFields } = state;

  const keyUpdater = ({ path, value }) => {
    if (value !== undefined) {
      keyValuesUpdated.add(path);
    } else {
      keyValuesUpdated.delete(path);
    }
    setKeyValuesUpdated(keyValuesUpdated);
  };

  const notify = (message: TTextType) => {
    dispatch(globalActions.notification(message));
  };

  //#region Handlers
  const onChangeReadyToSubmit = ready => setState({ isReadyToSubmit: ready });

  const handleChange = (path: string, value: any) => {
    onChangeObject(setValues, path, value, normalize, notify);
    keyUpdater({ path, value });
  };

  const handleCallbackChange = (path: string) => (value: any) => {
    onChangeObject(setValues, path, value, normalize, notify);
    keyUpdater({ path, value });
  };

  const handleSubmit = () => {
    if (props.zodValidationSchema) {
      log.info('zodValidationSchema', values);
      const result = props.zodValidationSchema.safeParse(values);
      if (result.success) {
        submitValues(methodCRUD === 'patch' ? result.data : values);
      } else {
        const firstError = result.error.errors[0].message;
        const errorFields: string[] = Object.keys(result.error.format());
        dispatch(globalActions.notification(firstError));
        setState({ requiredFields: errorFields });
      }
    } else if (props.validationSchema) {
      log.info('validationSchema', values);
      props.validationSchema
        .validate(values, { abortEarly: false, context: values })
        .then(() => {
          const castedValues = props.validationSchema.cast(values);
          submitValues(methodCRUD === 'patch' ? castedValues : values);
        })
        .catch(err => {
          console.log('values', values);
          console.log('err', err);

          const missingFieldLabels: string[] = [];
          const requiredFields: string[] = [];
          let notificationMsg: TTextType[] = [];
          if (err.inner) {
            console.log('err.inner', err.inner);
            err.inner.forEach(inError => {
              requiredFields.push(inError.path);
              if (inError.type === 'validation') {
                notificationMsg = ['Required field missing', ': ', inError.message];
              } else if (inError.type !== 'required') {
                notificationMsg = inError.message;
              } else {
                missingFieldLabels.push(inError.params.label || inError.params.path);

                notificationMsg = ['Required field missing', ': ', missingFieldLabels.join(', ')];
              }
            });
          }
          dispatch(globalActions.notification(notificationMsg));
          setState({ requiredFields });
        });
    } else submitValues(values);
  };

  const handleCancel = () => {
    removeDocuments();
    // Restart the state to initial values
    /* const nextState = produce(state, draft => {
      draft.values = props.initialValues;
      draft.updated = new Set();
    });

    setState(nextState); */
    setValues(props.initialValues);
    if (props.onCancel) {
      props.onCancel();
    } else {
      window.history.back();
    }
  };
  //#endregion

  //#region Functions
  const submitValues = values => {
    const { essentialValues } = props;

    let attrs = {};
    if (mode === 'new' || methodCRUD === 'put') attrs = values;
    else {
      if (essentialValues) {
        const union = new Set([...essentialValues, ...keyValuesUpdated]);
        union.forEach(path => {
          const value = get(values, path, null);
          set(attrs, path, value);
        });
      } else {
        attrs = values;
      }
    }

    keyValuesUpdated.clear();
    setKeyValuesUpdated(keyValuesUpdated);

    log.info('submitValues', attrs);
    props.onSubmit(attrs);
  };

  const removeDocuments = () => {
    if (keyValuesUpdated.has('documents')) {
      const { documents } = values;
      documents.forEach(document => document.temporary && dispatch(decommissionData({ url: '/api/document/' + document.id })));
    }
  };

  //#endregion

  //#region Renders
  const renderActionsButtons = () => {
    return (
      <Grid
        id="form-container-footer"
        container
        size={'auto'}
        justifyContent="space-between"
        alignItems="center"
        className="w-full"
      >
        <Grid>
          {renderActionButtons()}
          {renderSubmissionButtons()}
        </Grid>
        <Grid>
          <AuditDataEntity mode={mode} values={values} disabledNotifyChanges={disabledNotifyChanges} />
        </Grid>
      </Grid>
    );
  };

  const renderAlertChangeEntity = () => {
    if (keyValuesUpdated.size === 0) return;
    if (!disabledNotifyChanges) return;
    return (
      <Alert severity="warning" className="ml-4 p-0 px-2 bg-amber-100 border-2 border-amber-200">
        <Typography className="text-sm " variant="body2" component={'span'} text={'There are unsaved changes'} />
      </Alert>
    );
  };

  const renderSubmissionButtons = () => {
    if (mode === 'view') return;
    const { isReadyToSubmit } = state;

    return (
      <Grid container>
        <Button
          id={`formContainerSave`}
          variant="text"
          onClick={handleSubmit}
          disabled={props.isSubmiting || !isReadyToSubmit}
          label={submitButtonCaption}
        />
        <Button variant="text" id={`formContainerCancel`} onClick={handleCancel} label={cancelButtonCaption} />
        {renderAlertChangeEntity()}
      </Grid>
    );
  };

  //#endregion

  useEffect(() => {
    return () => {
      removeDocuments();
    };
  }, []);

  useEffect(() => {
    //setState({ ...state, values: props.initialValues });
    setValues(props.initialValues);
  }, [props.initialValues]);

  if (!values) return;
  log.info('values', values);
  return (
    <GridContainerFullHeight data-id="formContainer">
      <Grid data-id="formContainerBody" size={'grow'} p={2} sx={{ overflowY: 'auto' }}>
        <ChildForm
          {...props}
          onChange={handleChange}
          onCallbackChange={handleCallbackChange}
          onChangeReadyToSubmit={onChangeReadyToSubmit}
          mode={mode} // may be needed for conditionar rendering between update and new cases
          values={values}
          requiredFields={requiredFields}
        />
      </Grid>
      <Divider className="w-full" />
      {renderActionsButtons()}
    </GridContainerFullHeight>
  );
};

export default FormContainer;
